import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { AdBannerInFeed } from '../components/AdBanner';
import { RandomProducts } from '../components/RandomProducts';
import { ArticleCards } from '../components/ArticleCards';
import { ProductReviews } from '../components/ProductReviews';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Flame, ThumbsUp, ThumbsDown, ExternalLink, Clock, ArrowLeft, Send, Loader2, MessageSquare, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { pl } from 'date-fns/locale';
import { toast } from 'sonner';

export default function DealDetailPage() {
  const { id } = useParams();
  const { token, authHeaders } = useAuth();
  const [deal, setDeal] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [myReaction, setMyReaction] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dealRes, commentsRes] = await Promise.all([
          axios.get(`${API}/deals/${id}`),
          axios.get(`${API}/deals/${id}/comments`)
        ]);
        setDeal(dealRes.data);
        setComments(commentsRes.data.comments);
        
        if (token) {
          try {
            const reactionRes = await axios.get(`${API}/deals/${id}/my-reaction`, { headers: authHeaders() });
            setMyReaction(reactionRes.data.reaction);
          } catch {}
        }
      } catch {
        toast.error('Nie udało się załadować okazji');
      }
      setLoading(false);
    };
    fetchData();
  }, [id, token, authHeaders]);

  const handleReact = async (type) => {
    if (!token) { toast.error('Zaloguj się'); return; }
    try {
      const res = await axios.post(`${API}/deals/${id}/react`, { type }, { headers: authHeaders() });
      setMyReaction(res.data.status === 'removed' ? null : res.data.type);
      // Refresh deal for updated counts
      const dealRes = await axios.get(`${API}/deals/${id}`);
      setDeal(dealRes.data);
    } catch {}
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    if (!token) { toast.error('Zaloguj się aby komentować'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/deals/${id}/comments`, { text: newComment }, { headers: authHeaders() });
      setComments([res.data, ...comments]);
      setNewComment('');
      toast.success('+2 Zorki za komentarz!');
    } catch {
      toast.error('Nie udało się dodać komentarza');
    }
    setSending(false);
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 text-primary animate-spin" />
    </div>
  );

  if (!deal) return (
    <div className="text-center py-20 text-muted-foreground">
      Okazja nie znaleziona
    </div>
  );

  const imageUrl = deal.image_local_path
    ? `${process.env.REACT_APP_BACKEND_URL}${deal.image_local_path}`
    : deal.image_url;

  const displayDesc = deal.ai_description || deal.description;
  const hasSeoDesc = deal.seo_description && deal.seo_description.length > 10;
  const hotCount = deal.reactions?.hot || 0;
  const coldCount = deal.reactions?.cold || 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="deal-detail-page">
      <SEO
        title={deal.title}
        description={displayDesc?.slice(0, 160)}
        image={imageUrl}
        type="product"
      />
      {/* Back */}
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-4 transition-colors" data-testid="back-to-feed">
        <ArrowLeft className="w-4 h-4" /> Wróć do Gadulca
      </Link>

      <div className="bg-card border border-border/40 overflow-hidden">
        {/* Image */}
        {imageUrl && (
          <div className="w-full h-64 sm:h-80 bg-secondary overflow-hidden relative">
            <img src={imageUrl} alt={deal.title} className="w-full h-full object-cover" />
            {deal.deal_price && (
              <div className="absolute bottom-4 left-4 bg-primary text-primary-foreground px-4 py-2">
                <span className="price-tag text-3xl">{deal.deal_price} zł</span>
                {deal.original_price && deal.original_price > deal.deal_price && (
                  <span className="text-sm line-through ml-2 opacity-70">{deal.original_price} zł</span>
                )}
              </div>
            )}
          </div>
        )}

        <div className="p-6">
          {/* Title */}
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-2">
            {deal.title}
          </h1>

          {/* Meta */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground mb-4">
            <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {deal.user_nick || 'Anonim'}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {deal.created_at ? formatDistanceToNow(new Date(deal.created_at), { addSuffix: true, locale: pl }) : ''}
            </span>
            {deal.category && <span className="text-primary/80">{deal.category}</span>}
            {deal.imported_from_xml && (
              <span className="text-xs bg-secondary px-2 py-0.5 text-muted-foreground">Import XML</span>
            )}
          </div>

          {/* Price (no image) */}
          {!imageUrl && deal.deal_price && (
            <div className="mb-4">
              <span className="price-tag text-4xl text-primary">{deal.deal_price} zł</span>
              {deal.original_price && deal.original_price > deal.deal_price && (
                <span className="text-lg text-muted-foreground line-through ml-3">{deal.original_price} zł</span>
              )}
            </div>
          )}

          {/* Description */}
          {hasSeoDesc ? (
            <div className="seo-content mb-6" data-testid="deal-seo-description">
              <div dangerouslySetInnerHTML={{ __html: deal.seo_description }} />
            </div>
          ) : displayDesc ? (
            <div className="text-foreground/90 leading-relaxed whitespace-pre-wrap mb-6">
              {displayDesc}
            </div>
          ) : null}

          {/* Actions bar */}
          <div className="flex items-center justify-between border-t border-border/30 pt-4">
            <div className="flex items-center gap-4">
              <button
                data-testid="detail-react-hot"
                onClick={() => handleReact('hot')}
                className={`flex items-center gap-1.5 px-3 py-1.5 border transition-colors ${
                  myReaction === 'hot' ? 'border-primary text-primary bg-primary/10' : 'border-border/40 text-muted-foreground hover:border-primary/30'
                }`}
              >
                <ThumbsUp className="w-4 h-4" />
                <span className="font-heading font-bold">{hotCount}</span>
              </button>
              <button
                data-testid="detail-react-cold"
                onClick={() => handleReact('cold')}
                className={`flex items-center gap-1.5 px-3 py-1.5 border transition-colors ${
                  myReaction === 'cold' ? 'border-blue-400 text-blue-400 bg-blue-400/10' : 'border-border/40 text-muted-foreground hover:border-blue-400/30'
                }`}
              >
                <ThumbsDown className="w-4 h-4" />
                <span className="font-heading font-bold">{coldCount}</span>
              </button>
            </div>

            {(deal.affiliate_url || deal.url) && (
              <a
                href={deal.affiliate_url || deal.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="deal-buy-btn"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2 font-heading font-bold uppercase tracking-wider hover:bg-primary/90 transition-colors"
              >
                <ExternalLink className="w-4 h-4" /> Kup teraz
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Random products instead of ad */}
      <RandomProducts count={3} excludeId={deal?.id} className="my-4" />

      {/* Comments Section */}
      <div className="mt-6" data-testid="comments-section">
        <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary" />
          Komentarze ({comments.length})
        </h2>

        {/* Add comment */}
        {token ? (
          <form onSubmit={handleComment} className="flex gap-2 mb-6">
            <Input
              data-testid="comment-input"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Napisz komentarz..."
              className="flex-1 bg-input border-transparent focus:border-primary rounded-none"
            />
            <Button
              data-testid="comment-submit-btn"
              type="submit"
              disabled={sending}
              className="bg-primary text-primary-foreground rounded-none px-4"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground mb-6">
            <Link to="/auth" className="text-primary hover:underline">Zaloguj się</Link> aby komentować
          </p>
        )}

        {/* Comments list */}
        <div className="space-y-3">
          {comments.map(comment => (
            <div key={comment.id} className="bg-card border border-border/30 p-4" data-testid={`comment-${comment.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                <span className="text-sm font-medium text-white">{comment.user_nick}</span>
                <span className="text-xs text-muted-foreground">
                  {comment.created_at ? formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: pl }) : ''}
                </span>
              </div>
              <p className="text-sm text-foreground/80">{comment.text}</p>
            </div>
          ))}
          {comments.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">Brak komentarzy. Bądź pierwszy!</p>
          )}
        </div>
      </div>

      {/* Product Reviews */}
      {deal && <ProductReviews productId={deal.id} />}

      {/* Related blog articles */}
      {deal && (
        <ArticleCards
          title="Powiązane artykuły"
          context={`${deal.title} ${deal.category || ''} ${deal.description?.slice(0, 100) || ''}`}
          limit={4}
        />
      )}
    </div>
  );
}
